<template>
  <div v-if="parsedBuckets.length > 0" class ="pt-2">
    <div class="since-select ml-3" v-if="!isLocked">
      <div class="position-relative width--45per d-inline-block">
        <button type="button"
          class="btn btn-secondary bd-color-light width--100per d-flex justify-content-between align-items-center"
          @click.prevent="isActiveFrom = !isActiveFrom"
          v-click-outside="hideFrom"
        >
          <span v-if="from === undefined" class="t-secondary">From</span>
          <span v-else class="t-black">{{ from }}</span>
          <i class="t-black fa" :class="{ 'fa-angle-down': !isActiveFrom, 'fa-angle-up': isActiveFrom }" />
        </button>

        <div v-if="isActiveFrom" class="dropdown-menu-custom">
          <div v-for="(since, index) in parsedBuckets"
            :key="index"
            @click.prevent="handleFromSelected(since)"
            class="dropdown-item"
          >{{ since.label }}</div>
        </div>
      </div>

      <div class="position-relative width--45per ml-5p d-inline-block">
        <button type="button"
          class="btn btn-secondary bd-color-light width--100per d-flex justify-content-between align-items-center"
          @click.prevent="isActiveTo = !isActiveTo"
          v-click-outside="hideTo"
        >
          <span v-if="to === undefined" class="t-secondary">To</span>
          <span v-else class="t-black">{{ to }}</span>
          <i class="t-black fa" :class="{ 'fa-angle-down': !isActiveTo, 'fa-angle-up': isActiveTo }" />
        </button>

        <div v-if="isActiveTo" class="dropdown-menu-custom">
          <div v-for="(since, index) in parsedBuckets"
            :key="index"
            @click.prevent="handleToSelected(since)"
            class="dropdown-item"
          >{{ since.label }}</div>
        </div>
      </div>
    </div>

    <div class="ratio-7-4">
      <div :id="chartId" class="ratio-content" />
    </div>
  </div>
  <div v-else>
    <p class="t-secondary mb-0 t-sm px-3">
      No active filters for your query.
    </p>
  </div>
</template>

<script>
import helpers from '@/components/shared/chart/helpers.js'

import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'

export default {
  name: 'bucketSince',
  mixins: [helpers],
  components: {
  },
  props: {
    isFilterLoaded: {
      type: Boolean,
      default: false
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    filterName: {
      type: String,
      required: true
    },
    filter: {
      type: Object,
      required: true
    },
    index: {
      type: String,
      required: true
    },
    isMobileFilter: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isActiveFrom: false,
      isActiveTo: false,
      from: undefined,
      to: undefined,
      buckets: []
    }
  },
  computed: {
    parsedBuckets () {
      return this.buckets.filter(b => b.value && !b.value.includes('-'))
    },
    activeBucket () {
      return this.buckets.find(b => b.value && b.value.includes('-'))
    }
  },
  mounted () {
    if (this.isFilterLoaded) {
      this.buckets = [...this.filter.buckets]
      this.$nextTick(() => {
        this.prepareGraphData()
      })
    }
  },
  watch: {
    isFilterLoaded (newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.buckets = [...this.filter.buckets]
        this.prepareGraphData(true)
      }
    }
  },
  methods: {
    handleSince (bucket, filterName) {
      // If the selection we make is exactly the same as the one currently active, deselect the selection instead
      if (this.activeBucket && bucket.value === this.activeBucket.value) bucket.active = !bucket.active
      this.$emit('bucketSinceToggled', bucket, filterName)
    },
    hideTo () {
      this.isActiveTo = false
    },
    hideFrom () {
      this.isActiveFrom = false
    },
    handleToSelected (since) {
      if (!this.isEmpty(since)) {
        this.selectRange({ to: since.value })
      }
    },
    handleFromSelected (since) {
      if (!this.isEmpty(since)) {
        this.selectRange({ from: since.value })
      }
    },
    selectRange ({ from, to }) {
      let bucket = {
        active: false,
        count: 0
      }
      if (from !== undefined) {
        this.from = parseInt(from)
      }
      if (to !== undefined) {
        this.to = parseInt(to)
      }

      if (this.to !== undefined && this.from !== undefined) {
        if (this.from > this.to) {
          const temp = this.from
          this.from = this.to
          this.to = temp
        }
        const range = `${this.from}-${this.to}`
        const label = this.from !== this.to ? `Range ${range}` : this.to.toString()
        bucket.value = range
        bucket.label = label
      } else if (this.to !== undefined) {
        this.from = this.to
        const range = `${this.to}-${this.to}`
        bucket.value = range
        bucket.label = this.to.toString()
      } else if (this.from !== undefined) {
        this.to = this.from
        const range = `${this.from}-${this.from}`
        bucket.value = range
        bucket.label = this.from.toString()
      }

      this.handleSince(bucket, this.filterName)
    },
    prepareGraphData (updateData = false) {
      const activeBucket = this.buckets.find(b => b.active)
      if (activeBucket) {
        if (activeBucket.value.toString().includes('-')) {
          const range = [...activeBucket.value.split('-')]
          this.from = parseInt(range[0])
          this.to = parseInt(range[1])

          if (this.from !== this.to) {
            if (this.from > this.to) {
              const temp = this.from
              this.from = this.to
              this.to = temp
            }
            activeBucket.label = `Range ${this.from}-${this.to}`
          } else {
            activeBucket.label = this.from.toString()
          }
        } else {
          this.to = undefined
          this.from = parseInt(activeBucket.value)
        }
      } else {
        this.to = undefined
        this.from = undefined
      }

      // Filter and sort bucket data that we want to show on the graph
      const bucketData = this.buckets.filter(bucket => {
        const minYear = this.from && this.from < 2005 ? this.from : 2005
        const maxYear = this.to ? this.to : 2040
        return !bucket.value.toString().includes('-') && parseInt(bucket.value) >= minYear && parseInt(bucket.value) <= maxYear
      }).sort((a, b) => {
        return parseInt(a.value) - parseInt(b.value)
      })

      // If no data tyo show on graph, reset stuff
      if (!bucketData || bucketData.length === 0) {
        return
      }

      const startValue = parseInt(bucketData[0].value)
      // Add extra missing buckets if first year is >2005
      if (startValue > 2005) {
        for (let i = 2005; i < startValue; i++) {
          bucketData.unshift({
            value: i.toString(),
            count: 0,
            active: false
          })
        }
      }

      this.data = this.formatGraphData(bucketData, this.from, this.to)

      this.$nextTick(() => {
        if (updateData && this.series) {
          this.series.data.setAll(this.data)
          this.xAxis.data.setAll(this.data)
        } else {
          this.renderGraph(this.data)
        }
      })
    },
    formatGraphData (data, from = undefined, to = undefined) {
      if (!data) {
        return []
      }

      const items = data
        .reverse()
        .reduce((a, b, i, arr) => {
          const prevValue = i && arr[i - 1].value
          // Check if there are any gaps between years, add missing dataPoints if needed
          if (prevValue && prevValue < b.value - 1) {
            const count = b.x - prevValue - 1
            for (let j = 0; j < count; j++) {
              a.push({
                x: prevValue + j + 1,
                y: 0
              })
            }
          }
          // Include suggestion counts into the graph data
          a.push({
            x: b.value,
            y: b.count
          })
          return a
        }, [])

      return items.map(i => {
        let isActive = false
        // Set active flags on relevant items, to indicate the filter for that particular value is active
        if (
          to && from &&
          i.x >= from && i.x <= to
        ) {
          isActive = true
        }
        return {
          ...i,
          active: isActive
        }
      })
    },
    renderGraph (data) {
      // Only create root if not present
      if (!this.root) this.initRoot()
      const root = this.root

      this.setAxisStyle(root)
      // Init standard XY chart element
      const chart = this.initChartElement(root)

      const yAxis = this.generateYAxis({ root, chart })

      this.xAxis = this.generateXAxis({
        root,
        chart,
        minGridDistance: 30,
        categoryField: 'x'
      })
      this.xAxis.data.setAll(data)

      let tooltip
      if (!this.isMobileFilter) {
        tooltip = am5.Tooltip.new(root, {
          getFillFromSprite: false,
          getStrokeFromSprite: true,
          labelText: '[bold]{x}[/]\t {valueY} {name}\n\nClick to select year\nClick and drag to select year range',
          autoTextColor: false,
          scale: 0.94
        })

        tooltip.get('background').setAll({
          fill: am5.color(0x656F79),
          fillOpacity: 0.9,
          strokeOpacity: 0
        })

        tooltip.label.setAll({
          fill: am5.color(0xffffff),
          fontSize: '14px'
        })
      }

      this.series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: this.$options.filters.humanize(this.index),
          valueYField: 'y',
          categoryXField: 'x',
          xAxis: this.xAxis,
          yAxis: yAxis,
          fill: am5.color(0x43aec5),
          tooltip: this.isMobileFilter ? null : tooltip
        })
      )
      this.series.columns.template.set('width', am5.percent(80))
      this.series.data.setAll(data)

      this.series.columns.template.setAll({
        fill: am5.color(0x43aec5),
        fillOpacity: 0.24,
        visible: true,
        width: am5.percent(90)
      })

      this.series.columns.template.adapters.add('fillOpacity', function (fill, target) {
        if (target.dataItem.dataContext.active) {
          return 0.9
        } else {
          return fill
        }
      })

      // Locked functionality
      if (this.isLocked) {
        root.container.children.push(am5.Container.new(root, {
          width: am5.p100,
          height: am5.p100,
          layer: 1000,
          background: am5.Rectangle.new(this.root, {
            fill: am5.color(0xffffff),
            fillOpacity: 0.7
          })
        }))

        root.container.children.push(am5.Graphics.new(root, {
          fill: am5.color(0xC6C6C6),
          svgPath: `M165.3,99.4V88.7c0-7.9,6.4-14.3,14.3-14.3s14.3,6.4,14.3,14.3v10.7H165.3z M211.7,104.8c0-3-2.4-5.4-5.4-5.4
              h-1.8V88.7c0-13.7-11.3-25-25-25s-25,11.3-25,25v10.7h-1.8c-3,0-5.4,2.4-5.4,5.4v32.1c0,3,2.4,5.4,5.4,5.4h53.6 c3,0,5.4-2.4,5.4-5.4V104.8z`
        }))

        root.container.events.on('click', () => {
          this.$can.notAllowed()
        })

        return
      }

      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
        alwaysShow: false,
        xAxis: this.xAxis,
        behavior: 'selectX',
        snapToSeries: [ this.series ],
        snapToSeriesBy: 'x'
      }))

      cursor.lineY.setAll({
        visible: false
      })
      if (this.isMobileFilter) {
        cursor.lineX.setAll({
          visible: false
        })
      }

      this.bindClickEvents(cursor)
    },
    bindClickEvents (cursor) {
      // Hacky mobile fix, only allow single click, can not drag
      if (this.isMobileFilter) {
        cursor.events.on('selectended', (ev, t) => {
          // We calculate inversely (%-based, 100% - positionX), because data is inversed.
          const x = 1 - ev.target.getPrivate('positionX')
          // Index of seriesItem that is closest to selection
          const i = this.xAxis.axisPositionToIndex(x)

          this.selectRange({
            from: this.data[i].x,
            to: this.data[i].x
          })

          this.series.data.setAll(this.data.map((el, index) => {
            if (index === i) {
              el.active = true
            } else {
              el.active = false
            }
            return el
          }))
        })
        return
      }
      let startIndex = null
      let endIndex = null

      // "Click" event simulation, needs slightly different logic than selectStart/end (the initial click event triggers both)
      this.series.events.on('pointerup', (ev, t) => {
        setTimeout(() => {
          if (startIndex !== null && !isNaN(startIndex)) {
            this.selectRange({
              from: this.data[startIndex].x,
              to: this.data[startIndex].x
            })
          }
        }, 0)
      })

      cursor.events.on('selectstarted', (ev, t) => {
        const x = 1 - ev.target.getPrivate('positionX')
        // Index of seriesItem that is closest to selection
        const i = this.xAxis.axisPositionToIndex(x)
        startIndex = i
      })

      cursor.events.on('selectended', (ev, t) => {
        // We calculate inversely (%-based, 100% - positionX), because data is inversed.
        const x = 1 - ev.target.getPrivate('positionX')
        // Index of seriesItem that is closest to selection
        const i = this.xAxis.axisPositionToIndex(x)

        endIndex = i

        if (isNaN(startIndex)) {
          this.selectRange({
            from: this.data[endIndex].x,
            to: this.data[endIndex].x
          })
        } else {
          this.selectRange({
            from: this.data[startIndex].x,
            to: this.data[endIndex].x
          })

          startIndex = null
          endIndex = null
        }

        this.series.data.setAll(this.data.map((el, index) => {
          if (startIndex < endIndex) {
            if (index >= startIndex && index <= endIndex) {
              el.active = true
            } else {
              el.active = false
            }
          } else {
            if (index >= endIndex && index <= startIndex) {
              el.active = true
            } else {
              el.active = false
            }
          }
          return el
        }))
        // Hide the selected area on the chart component, we use `active` state now
        cursor.selection.hide()
      })
    }
  }
}
</script>
